import {Component, inject, OnInit, ViewChild} from '@angular/core';
import {CommonModule, Location} from '@angular/common';
import {ActivatedRoute, Router} from "@angular/router";
import {
    AccessType,
    AssetClassification,
    AssetCondition,
    AssetDto,
    AssetOwnership,
    AssetsClient,
    AssetType,
    AttachmentsDto,
    AuthService,
    CreateAssetCommand,
    OwnershipInfoDto,
    PermissionType,
    UpdateAssetCommand
} from "@services";
import {
    DxButtonModule,
    DxDateBoxModule,
    DxLoadPanelModule,
    DxSelectBoxModule,
    DxTextBoxModule,
    DxToolbarModule,
    DxValidationGroupComponent,
    DxValidationGroupModule,
    DxValidatorModule,
    DxScrollViewModule
} from "devextreme-angular";
import EnumUtils from "../../../../shared/enum-utils";
import notify from "devextreme/ui/notify";
import {DataSources} from "@services";
import {AssetAttachmentSelectorComponent} from "./components/asset-attachment-selector/asset-attachment-selector.component";
import {AssetTypePipe} from "../../../../pipes/asset-type.pipe";
import {CustomStore} from "devextreme-aspnet-data-nojquery";
import {AssetClassificationPipe} from "../../../../pipes/asset-classification.pipe";
import {NotesComponent, NoteType} from "../../../../components/library/notes/notes.component";

@Component({
    selector: 'app-asset-details',
    imports: [
        CommonModule,
        DxToolbarModule,
        DxButtonModule,
        DxLoadPanelModule,
        DxValidationGroupModule,
        DxTextBoxModule,
        DxValidatorModule,
        DxSelectBoxModule,
        DxDateBoxModule,
        DxScrollViewModule,
        NotesComponent,
        AssetAttachmentSelectorComponent,
        NotesComponent
    ],
    providers: [AssetTypePipe, AssetClassificationPipe],
    templateUrl: './asset-details.component.html',
    styleUrl: './asset-details.component.scss',
})
export class AssetDetailsComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private location = inject(Location);
    private assetsClient = inject(AssetsClient);
    private dataSources = inject(DataSources);
    private router = inject(Router);
    protected authService = inject(AuthService);
    private assetTypePipe = inject(AssetTypePipe);
    private assetClassificationPipe = inject(AssetClassificationPipe);

    @ViewChild('validationGroup', {static: true}) validationGroup: DxValidationGroupComponent;
    @ViewChild(NotesComponent) notesComponent: NotesComponent;

    protected assetId: string | null = null;
    protected asset: AssetDto = new AssetDto();
    protected isLoading = false;
    protected isSubmitting = false;

    protected assetTypeOptions = EnumUtils.getEnumOptions(AssetType, (v) => this.assetTypePipe.transform(v));
    protected assetOwnershipOptions = EnumUtils.getEnumOptions(AssetOwnership);
    protected assetConditionOptions = EnumUtils.getEnumOptions(AssetCondition);
    protected assetClassificationOptions = EnumUtils.getEnumOptions(AssetClassification,
        (v) => this.assetClassificationPipe.transform(v));

    protected readonly PermissionType = PermissionType;
    protected readonly AccessType = AccessType;

    protected readonly AssetOwnership = AssetOwnership;
    protected readonly AssetType = AssetType;
    protected readonly NoteType = NoteType;

    protected printerOptionsDataSource: CustomStore;
    protected tabletOptionsDataSource: CustomStore;
    protected scannerOptionsDataSource: CustomStore;
    protected batteryOptionsDataSource: CustomStore;
    protected clampOptionsDataSource: CustomStore;
    protected slipSheetAttachDataSource: CustomStore;

    ngOnInit(): void {
        this.assetId = this.route.snapshot.paramMap.get('assetId');
        if (this.assetId) {
            this.initializeDataSources(this.assetId);
            this.loadAsset();
        } else {
            this.initializeDataSources();
            this.initializeNewAsset();
        }
    }

    private initializeDataSources(assetId: string | null = null){
        this.printerOptionsDataSource = this.dataSources.assetOptions(AssetType.MobileLabelPrinter, true, assetId);
        this.tabletOptionsDataSource = this.dataSources.assetOptions(AssetType.TabletComputers, true, assetId);
        this.scannerOptionsDataSource = this.dataSources.assetOptions(AssetType.RfScanGuns, true, assetId);
        this.batteryOptionsDataSource = this.dataSources.assetOptions(AssetType.MheBatteries, true, assetId);
        this.clampOptionsDataSource = this.dataSources.assetOptions(AssetType.ClampLayerAttach, true, assetId);
        this.slipSheetAttachDataSource = this.dataSources.assetOptions(AssetType.SlipSheetAttach, true, assetId);
    }

    private initializeNewAsset() {
        this.asset = new AssetDto();
        this.asset.ownershipInfo = new OwnershipInfoDto();
        this.asset.attachments = new AttachmentsDto();
    }

    private loadAsset() {
        this.isLoading = true;
        this.assetsClient.getAsset(this.assetId!).subscribe({
            next: (data) => {
                this.asset = data;
                if (!this.asset.ownershipInfo) this.asset.ownershipInfo = new OwnershipInfoDto();
                if (!this.asset.attachments) this.asset.attachments = new AttachmentsDto();
                this.isLoading = false;
            },
            error: () => {
                notify('Failed to load asset', 'error', 3000);
                this.isLoading = false;
                this.location.back();
            }
        });
    }

    save() {
        const result = this.validationGroup.instance.validate();
        if (!result.isValid) {
            notify({message: 'Please fix validation errors', position: 'bottom center'}, 'error', 3000);
            return;
        }

        this.isSubmitting = true;

        if (this.assetId) {
            this.assetsClient.updateAsset(new UpdateAssetCommand({...this.asset, id: this.assetId})).subscribe({
                next: () => {
                    notify('Asset updated successfully', 'success', 3000);
                    this.isSubmitting = false;
                },
                error: (errors: Array<any>) => {
                    const msg = errors?.[0]?.message || 'Failed to update asset';
                    notify(msg, 'error', 3000);
                    this.isSubmitting = false;
                }
            });
        } else {
            this.assetsClient.createAsset(new CreateAssetCommand({...this.asset})).subscribe({
                next: async (id) => {
                    if (this.notesComponent) {
                        await this.notesComponent.saveQueuedNotes(id);
                    }
                    notify('Asset created successfully', 'success', 3000);
                    this.isSubmitting = false;
                    await this.router.navigate(['/assets/edit', id], {replaceUrl: true});
                },
                error: (errors: Array<any>) => {
                    const msg = errors?.[0]?.message || 'Failed to save asset';
                    notify(msg, 'error', 3000);
                    this.isSubmitting = false;
                }
            });
        }
    }

    cancel() {
        this.location.back();
    }

    get showOwnershipInfo(): boolean {
        return this.asset.ownership === AssetOwnership.Lease ||
            this.asset.ownership === AssetOwnership.Rental;
    }

    get showAttachments(): boolean {
        const type = this.asset.type;
        return [
            AssetType.MhBtS,
            AssetType.MhCounterBalance,
            AssetType.MhCounterBalanceLayPick,
            AssetType.MhReach,
            AssetType.MhWalkies
        ].includes(type!);
    }

    get showMobileLabelPrinter(): boolean {
        return [
            AssetType.MhBtS,
            AssetType.MhReach
        ].includes(this.asset.type!);
    }

    get showTabletComputer(): boolean {
        return [
            AssetType.MhBtS,
            AssetType.MhCounterBalanceLayPick,
            AssetType.MhCounterBalance,
            AssetType.MhReach,
        ].includes(this.asset.type!);
    }

    get tabletComputerRequired(): boolean {
        return [
            AssetType.MhBtS,
            AssetType.MhCounterBalanceLayPick,
            AssetType.MhReach,
        ].includes(this.asset.type!);
    }

    get showRfScanGun(): boolean {
        return [
            AssetType.MhBtS,
            AssetType.MhCounterBalance,
            AssetType.MhCounterBalanceLayPick,
            AssetType.MhReach
        ].includes(this.asset.type!);
    }

    get showMheBattery(): boolean {
        return [
            AssetType.MhBtS,
            AssetType.MhCounterBalance,
            AssetType.MhCounterBalanceLayPick,
            AssetType.MhReach,
            AssetType.MhWalkies
        ].includes(this.asset.type!);
    }

    get showClampLayerAttach(): boolean {
        return [
            AssetType.MhCounterBalanceLayPick,
        ].includes(this.asset.type!);
    }

    get showSlipSheetAttach(): boolean {
        return [
            AssetType.MhCounterBalance
        ].includes(this.asset.type!);
    }
}
